/**
 * Consistent chip for displaying a Lightspeed product's type bucket.
 *
 * Renders an MUI Chip with an icon + short label; the long label shows in a
 * tooltip. Used anywhere a product's type is surfaced — Product List Usage
 * Audit tables, product-setup exception configs, and anywhere else we later
 * adopt it.
 *
 * Product-type buckets, labels, colours, and icons live in
 * ``utils/productType.ts``.
 */
import React from 'react'
import { Chip, Tooltip } from '@mui/material'
import { ProductType, PRODUCT_TYPE_META } from '../utils/productType'

interface Props {
  productType: ProductType | null | undefined
  size?: 'small' | 'medium'
  showIcon?: boolean
  variant?: 'short' | 'long'
  /** When true, chip sizes to its content (no minWidth) — use on dense
   *  tables where a fixed min-width wastes horizontal space. */
  dense?: boolean
  /** Icon-only, no label — tooltip carries the long name. Compact chip
   *  for narrow columns (product-exception tables). */
  iconOnly?: boolean
}

const ProductTypeChip: React.FC<Props> = ({
  productType,
  size = 'small',
  showIcon = true,
  variant = 'short',
  dense = false,
  iconOnly = false,
}) => {
  if (!productType || !(productType in PRODUCT_TYPE_META)) {
    if (iconOnly) {
      return (
        <Tooltip title="Unknown type" arrow>
          <Chip
            size={size}
            label="-"
            variant="outlined"
            sx={{ width: 28, height: 22, color: 'text.secondary', '& .MuiChip-label': { px: 0.5 } }}
          />
        </Tooltip>
      )
    }
    return (
      <Chip
        size={size}
        label="-"
        variant="outlined"
        sx={{ minWidth: dense ? 0 : 80, fontWeight: 500, color: 'text.secondary' }}
      />
    )
  }

  const meta = PRODUCT_TYPE_META[productType]
  const Icon = meta.IconComponent
  const label = variant === 'long' ? meta.long : meta.short

  const textColor = meta.colorHex ?? undefined
  const isSmall = size === 'small'

  if (iconOnly) {
    return (
      <Tooltip title={meta.long} arrow>
        <Chip
          size={size}
          variant="outlined"
          label={<Icon sx={{ fontSize: 16, display: 'block' }} />}
          sx={{
            width: 28,
            height: 22,
            ...(textColor ? { color: textColor, borderColor: textColor } : {}),
            '& .MuiChip-label': {
              px: 0,
              py: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              ...(textColor ? { color: textColor } : {}),
            },
          }}
        />
      </Tooltip>
    )
  }

  return (
    <Tooltip title={meta.long} arrow>
      <Chip
        size={size}
        variant="outlined"
        icon={showIcon ? <Icon sx={{ fontSize: isSmall ? 14 : 18 }} /> : undefined}
        label={label}
        sx={{
          minWidth: dense ? 0 : 110,
          fontWeight: 500,
          fontSize: isSmall ? 12 : 13,
          height: isSmall ? 22 : 28,
          ...(textColor ? { color: textColor } : {}),
          // Tight "tag" rhythm: icon hugs the left border, 4px icon-to-text
          // gap via (label pl 6) + (icon mr -2), symmetric 6px gutters.
          '& .MuiChip-label': { pl: 0.75, pr: 1, lineHeight: 1.15 },
          '& .MuiChip-icon': {
            ml: 0.75,
            mr: -0.25,
            ...(textColor ? { color: textColor } : {}),
          },
        }}
      />
    </Tooltip>
  )
}

export default ProductTypeChip
